import { Injectable } from '@angular/core';
import { ClassCreature, LeagueTier } from '../interfaces/creature.interface';
import { getLevelFromXP, getLeagueTier } from '../data/league-config';

@Injectable({ providedIn: 'root' })
export class GameStatePersistenceService {

  private getKey(classId: number, dataType: string): string {
    const schoolId = localStorage.getItem('schoolId') || '0';
    return `gameMode_${schoolId}_${classId}_${dataType}`;
  }

  saveCreature(classId: number, creature: ClassCreature): void {
    localStorage.setItem(this.getKey(classId, 'creature'), JSON.stringify(creature));
  }

  getCreature(classId: number): ClassCreature | null {
    const data = localStorage.getItem(this.getKey(classId, 'creature'));
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  updateXP(classId: number, xpToAdd: number): ClassCreature | null {
    const creature = this.getCreature(classId);
    if (!creature) return null;

    creature.totalXP += xpToAdd;
    const newLevel = getLevelFromXP(creature.totalXP);
    const leveledUp = newLevel > creature.level;
    creature.level = newLevel;
    creature.league = getLeagueTier(newLevel);

    this.saveCreature(classId, creature);
    return creature;
  }

  updateStreak(classId: number, isCorrect: boolean): ClassCreature | null {
    const creature = this.getCreature(classId);
    if (!creature) return null;

    if (isCorrect) {
      creature.currentStreak++;
      creature.totalCorrect++;
      if (creature.currentStreak > creature.bestStreak) {
        creature.bestStreak = creature.currentStreak;
      }
    } else {
      creature.currentStreak = 0;
    }
    creature.totalAnswered++;

    this.saveCreature(classId, creature);
    return creature;
  }

  createNewCreature(classId: number, creatureTypeId: string, customName: string, className?: string): ClassCreature {
    const creature: ClassCreature = {
      creatureTypeId,
      customName,
      totalXP: 0,
      level: 0,
      league: LeagueTier.WOOD,
      currentStreak: 0,
      bestStreak: 0,
      totalCorrect: 0,
      totalAnswered: 0,
      classId,
      className
    };
    this.saveCreature(classId, creature);
    return creature;
  }

  hasCreature(classId: number): boolean {
    return !!this.getCreature(classId);
  }

  clearGameData(classId: number): void {
    const schoolId = localStorage.getItem('schoolId') || '0';
    const prefix = `gameMode_${schoolId}_${classId}_`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
}
